import {
  COLLECT_METRICS,
  METRIC_COLLECTION_INTERVAL,
} from '../utils/constants';
import { pushStats } from '../actions/PushStats';

class ConsumerStats {
  constructor(token, socket) {
    this.consumers = {};
    this.interval = null;
    this.token = token;
    this.start();
    this.mutedPeers = new Set();
    this.socket = socket;
    this.collect = COLLECT_METRICS; // Flag to enable/disable stats collection
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.consumers = {};
    this.mutedPeers = new Set();
  }

  addConsumer(consumer, kind, peerId) {
    this.consumers[consumer.id] = {
      consumer,
      peerId,
      kind,
      stats: {},
    };
  }

  removeConsumer(consumerId) {
    delete this.consumers[consumerId];
  }

  setMuted(peerId, muted) {
    if (muted) {
      this.mutedPeers.add(peerId);
    } else {
      this.mutedPeers.delete(peerId);
    }
  }

  start() {
    if (!this.collect) return;
    if (this.interval) return;

    this.interval = setInterval(async () => {
      await this.getStats();
      await this.push();
    }, METRIC_COLLECTION_INTERVAL);
  }

  resetStats(consumerId) {
    this.consumers[consumerId].stats = {};
  }

  async getStats() {
    for (const consumerId in this.consumers) {
      const consumer = this.consumers[consumerId].consumer;

      if (consumer.paused) {
        this.resetStats(consumerId);
        continue;
      }

      if (
        this.consumers[consumerId].kind === 'audio' &&
        this.mutedPeers.has(this.consumers[consumerId].peerId)
      ) {
        this.resetStats(consumerId);
        continue;
      }

      if (
        !consumer ||
        consumer.closed ||
        consumer.paused ||
        this.consumers[consumerId].muted
      ) {
        return;
      }

      const kind = consumer.kind;

      let serverStats;
      try {
        serverStats = await this.socket.request('consumerStats', {
          id: consumer.id,
        });
      } catch {
        serverStats = null;
      }

      if (!serverStats || !serverStats.hasOwnProperty('stats')) continue;
      const activeSSRCs = new Set();

      serverStats.stats.forEach((stat) => {
        if (stat.type !== 'outbound-rtp') return;
        this.consumers[consumerId].stats[stat.ssrc] = {};
        this.consumers[consumerId].stats[stat.ssrc].bitrate = stat.bitrate;
        this.consumers[consumerId].stats[stat.ssrc].score = stat.score;
        this.consumers[consumerId].stats[stat.ssrc].roundTripTime =
          stat.roundTripTime;
        this.consumers[consumerId].stats[stat.ssrc].jitter = stat.jitter;
        this.consumers[consumerId].stats[stat.ssrc].fractionLost =
          stat.fractionLost;
        activeSSRCs.add(stat.ssrc); // Mark SSRC as active
      });

      if (kind === 'video') {
        const stats = Array.from((await consumer.getStats()).values());
        stats.forEach((stat) => {
          if (
            stat.type === 'inbound-rtp' &&
            stat.kind === kind &&
            stat.mid !== 'probator'
          ) {
            if (this.consumers[consumerId].stats[stat.ssrc]) {
              // if the layer is not in the server stats it is not being consumed
              this.consumers[consumerId].stats[stat.ssrc].fps =
                stat.framesPerSecond;
            }
          }
        });
      }

      // Cleanup: Remove SSRCs that were not found in this cycle or if there are no stats
      Object.keys(this.consumers[consumerId].stats).forEach((ssrc) => {
        if (
          !activeSSRCs.has(parseInt(ssrc)) ||
          Object.keys(this.consumers[consumerId].stats[ssrc]).length === 0
        ) {
          delete this.consumers[consumerId].stats[ssrc];
        }
      });
    }
  }

  async push() {
    const consumers = [];
    for (const consumerId in this.consumers) {
      const consumer = this.consumers[consumerId].consumer;
      const kind = this.consumers[consumerId].kind;

      // Only push if there are stats
      if (Object.keys(this.consumers[consumerId].stats).length === 0) continue;

      consumers.push({
        id: consumer.id,
        kind,
        producerId: consumer.producerId,
        stats: this.consumers[consumerId].stats,
      });
    }

    if (consumers.length === 0) return;

    await pushStats(
      'consumer-stats',
      {
        consumers,
      },
      this.token
    );
  }
}

export default ConsumerStats;
